/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import FilterFavorites, {
  FilterFavoriteListItem
} from '@ion/dls/src/filters/FilterFavorites';
import {
  TrackingFavoriteFilterState,
  TrackingFavoritePreferences
} from './types';

export interface TrackingFavoritesFilterProps {
  dirtyFavoriteName?: string | null;
  favorites: TrackingFavoritePreferences;
  selectedFavoriteName?: string | null;
  onDelete: (name: string) => void;
  onEdit: (name: string) => void;
  onRename: (name: string) => void;
  onReset: (name: string) => void;
  onSaveChanges: (name: string) => void;
  onSelect: (name: string, state: TrackingFavoriteFilterState) => void;
}

const getFavoriteItems = (
  favorites: TrackingFavoritePreferences,
  selectedFavoriteName?: string | null,
  dirtyFavoriteName?: string | null
): FilterFavoriteListItem[] => (
  Object.keys(favorites).map(name => ({
    dirty    : name === dirtyFavoriteName,
    name,
    selected : name === selectedFavoriteName
  }))
);

/**
 * Connects tracking favorite filters to the shared DLS favorites list.
 *
 * ```typescript
 * import TrackingFavoritesFilter from './TrackingFavoritesFilter';
 *
 * ...
 *
 * <TrackingFavoritesFilter favorites={favorites}
 *   onSelect={loadFavorite} />
 * ```
 */
export const TrackingFavoritesFilter = ({
  dirtyFavoriteName,
  favorites,
  selectedFavoriteName,
  onDelete,
  onEdit,
  onRename,
  onReset,
  onSaveChanges,
  onSelect
}: TrackingFavoritesFilterProps) => {
  const onSelectChange = useCallback((name: string) => {
    onSelect(name, favorites[name]);
  }, [favorites, onSelect]);

  return (
    <FilterFavorites items={getFavoriteItems(favorites, selectedFavoriteName, dirtyFavoriteName)}
      labels={{
        delete                 : translate('filters:tracking.favorites.menu.delete'),
        dirtyMessage           : translate('filters:tracking.favorites.dirty.message'),
        dirtyPrefix            : translate('filters:tracking.favorites.dirty.prefix'),
        edit                   : translate('filters:tracking.favorites.menu.edit'),
        emptyDescriptionPrefix : translate('filters:tracking.favorites.empty.descriptionPrefix'),
        emptyDescriptionSuffix : translate('filters:tracking.favorites.empty.descriptionSuffix'),
        emptyTitle             : translate('filters:tracking.favorites.empty.title'),
        rename                 : translate('filters:tracking.favorites.menu.rename'),
        reset                  : translate('filters:tracking.favorites.dirty.reset'),
        saveChanges            : translate('filters:tracking.favorites.dirty.saveChanges')
      }}
      onDelete={onDelete}
      onEdit={onEdit}
      onRename={onRename}
      onReset={onReset}
      onSaveChanges={onSaveChanges}
      onSelect={onSelectChange}
      // Edit has no settled flow yet, so it is left out of the menu for now ...
      hideEdit />
  );
};

export default memo(TrackingFavoritesFilter);
